// src/components/modules/ConversationModuleDetail.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  Clock,
  CheckCircle,
  ChevronRight,
  Target,
  Brain,
  BookOpen,
  Award,
  Lightbulb,
  Users,
  MessageSquare,
  ArrowRight,
  Play,
  Pause,
  RotateCcw,
  FileText,
  Beaker,
  TrendingUp,
  AlertCircle,
  Sparkles,
  Heart,
  Coffee,
  Eye,
  PartyPopper,
  Microscope,
  Zap
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';
import CompletionCertificate from './CompletionCertificate';
import { setReadingSection } from '../../services/companionModuleContext';

// Celebration Component
const CelebrationModal = ({ show, onClose, message = "Section Complete!" }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 1000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          exit={{ scale: 0, rotate: 180 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-gradient-to-br from-green-500 to-emerald-600 p-8 rounded-2xl shadow-2xl"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex items-center justify-center mb-4"
          >
            <CheckCircle className="w-20 h-20 text-white" strokeWidth={2.5} />
          </motion.div>
          <h2 className="text-3xl font-bold text-white text-center mb-2">{message}</h2>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 1 }}
            className="text-center"
          >
            <Sparkles className="w-8 h-8 text-yellow-300 mx-auto" />
          </motion.div>
        </motion.div>

        {/* Confetti particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: "50vw",
              y: "50vh",
              opacity: 1,
              scale: 1
            }}
            animate={{
              x: `${Math.random() * 100}vw`,
              y: `${Math.random() * 100}vh`,
              opacity: 0,
              scale: 0,
              rotate: Math.random() * 360
            }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="absolute w-3 h-3 rounded-full"
            style={{
              backgroundColor: ['#FFD700', '#FF6B6B', '#4ECDC4', '#95E1D3', '#F38181'][i % 5]
            }}
          />
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

// Markdown parsing helper with special formatting blocks
const parseMarkdownText = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Handle special blocks first (STAT, QUOTE, RESEARCH, OBSERVATION, INSIGHT, COMPARE)
  // Match [TYPE:content_until_closing_bracket]
  const specialBlockRegex = /\[([A-Z]+):([^\]]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = specialBlockRegex.exec(text)) !== null) {
    // Add text before the special block
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index), key: `text-${lastIndex}` });
    }

    // Add the special block
    const [fullMatch, blockType, fullContent] = match;

    // Parse the content based on block type
    let parsedBlock = {
      type: blockType.toLowerCase(),
      key: `block-${match.index}`
    };

    if (blockType === 'COMPARE') {
      // COMPARE blocks have format: [COMPARE:weakTitle|weakContent|strongTitle|strongContent]
      // Find the first 3 pipe positions
      const pipes = [];
      for (let i = 0; i < fullContent.length; i++) {
        if (fullContent[i] === '|') {
          pipes.push(i);
          if (pipes.length === 3) break;
        }
      }

      if (pipes.length === 3) {
        parsedBlock.weakTitle = fullContent.slice(0, pipes[0]).trim();
        parsedBlock.weakContent = fullContent.slice(pipes[0] + 1, pipes[1]).trim();
        parsedBlock.strongTitle = fullContent.slice(pipes[1] + 1, pipes[2]).trim();
        parsedBlock.strongContent = fullContent.slice(pipes[2] + 1).trim();
      } else {
        // Fallback - treat as simple two-part split
        const pipeIndex = fullContent.indexOf('|');
        if (pipeIndex !== -1) {
          parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
          parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
        } else {
          parsedBlock.content = fullContent.trim();
        }
      }
    } else {
      // STAT and QUOTE blocks have format: [TYPE:title|content]
      const pipeIndex = fullContent.indexOf('|');
      if (pipeIndex !== -1) {
        parsedBlock.title = fullContent.slice(0, pipeIndex).trim();
        parsedBlock.content = fullContent.slice(pipeIndex + 1).trim();
      } else {
        parsedBlock.content = fullContent.trim();
      }
    }

    parts.push(parsedBlock);

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex), key: `text-${lastIndex}` });
  }

  // If no special blocks found, treat as simple text
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text, key: 'text-0' });
  }

  // Render each part
  return parts.map((part) => {
    if (part.type === 'stat') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-l-4 border-cyan-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <TrendingUp className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-cyan-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'quote') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-l-4 border-purple-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <MessageSquare className="w-6 h-6 text-purple-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-purple-300 text-sm mb-1">{part.title}</div>
              <div className="text-slate-200 italic leading-relaxed">"{parseSimpleMarkdown(part.content)}"</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'research') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 border-l-4 border-indigo-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Microscope className="w-6 h-6 text-indigo-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-indigo-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'observation') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-l-4 border-amber-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Eye className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-amber-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'insight') {
      return (
        <div key={part.key} className="my-6 p-4 bg-gradient-to-r from-violet-500/10 to-purple-500/10 border-l-4 border-violet-500 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Zap className="w-6 h-6 text-violet-400 flex-shrink-0 mt-1" />
            <div>
              <div className="font-bold text-violet-300 text-sm uppercase tracking-wider mb-1">{part.title}</div>
              <div className="text-slate-200 leading-relaxed">{parseSimpleMarkdown(part.content)}</div>
            </div>
          </div>
        </div>
      );
    }

    if (part.type === 'compare') {
      // New format with explicit fields
      if (part.weakTitle && part.strongTitle) {
        return (
          <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {part.weakTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.weakContent)}</div>
            </div>
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <div className="font-bold text-green-400 text-sm mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {part.strongTitle}
              </div>
              <div className="text-slate-300 text-sm">{parseSimpleMarkdown(part.strongContent)}</div>
            </div>
          </div>
        );
      }

      // Fallback for old format
      const [weak, strong] = (part.content || '').split('|').map(s => s.trim());
      return (
        <div key={part.key} className="my-6 grid md:grid-cols-2 gap-4">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="font-bold text-red-400 text-sm mb-2 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {part.title?.split('|')[0] || 'Weak'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(weak)}</div>
          </div>
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="font-bold text-green-400 text-sm mb-2 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              {part.title?.split('|')[1] || 'Strong'}
            </div>
            <div className="text-slate-300 text-sm">{parseSimpleMarkdown(strong)}</div>
          </div>
        </div>
      );
    }

    // Regular text with bold/italic
    return <span key={part.key}>{parseSimpleMarkdown(part.content)}</span>;
  });
};

// Helper to parse simple markdown (bold/italic) within text
const parseSimpleMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  // Handle bold (**text**)
  const parts = text.split(/(\*\*.*?\*\*)/);

  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return <strong key={`bold-${index}`} className="font-bold text-white">{boldText}</strong>;
    }

    // Handle italics (*text*)
    const italicParts = part.split(/(\*[^*]+\*)/);

    if (italicParts.length > 1) {
      return italicParts.map((subPart, subIndex) => {
        if (subPart.startsWith('*') && subPart.endsWith('*') && subPart.length > 2) {
          const italicText = subPart.slice(1, -1);
          return <em key={`${index}-italic-${subIndex}`} className="italic text-purple-300">{italicText}</em>;
        }
        return <span key={`${index}-text-${subIndex}`}>{subPart}</span>;
      });
    }

    return <span key={`text-${index}`}>{part}</span>;
  });
};

const MarkdownText = ({ children, className = "" }) => {
  if (!children) return null;

  // Split by double line breaks to create paragraphs
  const paragraphs = children.split('\n\n').filter(p => p.trim());

  return (
    <div className={className}>
      {paragraphs.map((paragraph, idx) => (
        <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
          {parseMarkdownText(paragraph)}
        </div>
      ))}
    </div>
  );
};

// Component for rendering sections with expandable content
const SectionContent = ({ section }) => {
  const [expandedParts, setExpandedParts] = useState({
    scientificFoundation: false,
    deepDive: false,
    practicalApplication: false,
    commonMistakes: false
  });

  const toggleExpanded = (part) => {
    setExpandedParts(prev => ({
      ...prev,
      [part]: !prev[part]
    }));
  };

  return (
    <Card className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h3 className="text-2xl font-bold text-white">{section.title}</h3>
          <span className="text-sm text-slate-400">({section.duration})</span>
        </div>
      </div>

      <div className="space-y-6">
        {/* Introduction */}
        <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6">
          <MarkdownText className="text-slate-200 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-3 first-letter:float-left">
            {section.content.intro}
          </MarkdownText>
        </div>

        {/* Main Content */}
        {section.content.mainContent && (
          <div>
            <MarkdownText className="text-slate-300 text-base leading-relaxed whitespace-pre-line">
              {section.content.mainContent}
            </MarkdownText>
          </div>
        )}

        {/* Scientific Foundation */}
        {section.content.scientificFoundation && (
          <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-2 border-blue-400/40 rounded-xl p-6 hover:border-blue-400/60 transition-all shadow-lg shadow-blue-500/10">
            <button
              onClick={() => toggleExpanded('scientificFoundation')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-blue-200 flex items-center gap-3 group-hover:text-blue-100 transition-colors">
                  <Beaker className="w-6 h-6 text-blue-400" />
                  {section.content.scientificFoundation.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.scientificFoundation ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-blue-400 transition-transform group-hover:text-blue-300 ${
                      expandedParts.scientificFoundation ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.scientificFoundation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4 space-y-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.scientificFoundation.content}
                    </MarkdownText>
                    
                    {/* Key Studies if present */}
                    {section.content.scientificFoundation.keyStudies && (
                      <div className="mt-6 space-y-3">
                        <h5 className="text-sm font-medium text-blue-300 uppercase tracking-wider">Key Research</h5>
                        {section.content.scientificFoundation.keyStudies.map((study, idx) => (
                          <div key={idx} className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/20">
                            <div className="text-white font-medium">{study.researcher}</div>
                            <div className="text-blue-200 text-sm">{study.institution}, {study.year}</div>
                            <div className="text-slate-300 text-sm mt-1">{study.finding}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Key Points */}
        {section.content.keyPoints && section.content.keyPoints.length > 0 && (
          <div>
            <h4 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Key Insights
            </h4>
            <div className="space-y-3">
              {section.content.keyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg">
                  <div className="w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-300 text-sm font-bold">{index + 1}</span>
                  </div>
                  <MarkdownText className="text-slate-300">
                    {point}
                  </MarkdownText>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deep Dive */}
        {section.content.deepDive && (
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-2 border-purple-400/40 rounded-xl p-6 hover:border-purple-400/60 transition-all shadow-lg shadow-purple-500/10">
            <button
              onClick={() => toggleExpanded('deepDive')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-purple-200 flex items-center gap-3 group-hover:text-purple-100 transition-colors">
                  <Brain className="w-6 h-6 text-purple-400" />
                  {section.content.deepDive.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-purple-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.deepDive ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-purple-400 transition-transform group-hover:text-purple-300 ${
                      expandedParts.deepDive ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.deepDive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.deepDive.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Practical Application */}
        {section.content.practicalApplication && (
          <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/40 rounded-xl p-6 hover:border-green-400/60 transition-all shadow-lg shadow-green-500/10">
            <button
              onClick={() => toggleExpanded('practicalApplication')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-green-200 flex items-center gap-3 group-hover:text-green-100 transition-colors">
                  <Lightbulb className="w-6 h-6 text-green-400" />
                  {section.content.practicalApplication.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-green-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.practicalApplication ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-green-400 transition-transform group-hover:text-green-300 ${
                      expandedParts.practicalApplication ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.practicalApplication && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.practicalApplication.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Common Mistakes */}
        {section.content.commonMistakes && (
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-2 border-amber-400/40 rounded-xl p-6 hover:border-amber-400/60 transition-all shadow-lg shadow-amber-500/10">
            <button
              onClick={() => toggleExpanded('commonMistakes')}
              className="w-full text-left group"
            >
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-bold text-amber-200 flex items-center gap-3 group-hover:text-amber-100 transition-colors">
                  <AlertCircle className="w-6 h-6 text-amber-400" />
                  {section.content.commonMistakes.title}
                </h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-amber-300 opacity-0 group-hover:opacity-100 transition-opacity">
                    {expandedParts.commonMistakes ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  <ChevronRight
                    className={`w-6 h-6 text-amber-400 transition-transform group-hover:text-amber-300 ${
                      expandedParts.commonMistakes ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>
            </button>
            
            <AnimatePresence>
              {expandedParts.commonMistakes && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="mt-4">
                    <MarkdownText className="text-slate-300 leading-relaxed whitespace-pre-line">
                      {section.content.commonMistakes.content}
                    </MarkdownText>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Card>
  );
};

// Exercise Card Component
const ExerciseCard = ({ exercise, isCompleted, onComplete, isActive, onClick }) => {
  const difficultyColors = {
    gentle: 'bg-green-500/20 text-green-300',
    moderate: 'bg-yellow-500/20 text-yellow-300',
    challenging: 'bg-red-500/20 text-red-300'
  };

  const typeIcons = {
    observation: Eye,
    solo_practice: Users,
    partner_practice: Users,
    reflection: Brain,
    conversation: MessageSquare
  };

  const ExerciseIcon = typeIcons[exercise.type] || Target;

  return (
    <Card 
      hover={true}
      className={`p-6 cursor-pointer transition-all ${
        isActive ? 'ring-2 ring-conversation-teal' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-conversation-teal/20 rounded-lg flex items-center justify-center flex-shrink-0">
          <ExerciseIcon className="w-6 h-6 text-conversation-teal" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-lg font-medium text-white">{exercise.title}</h4>
            {isCompleted && (
              <CheckCircle className="w-5 h-5 text-green-400" />
            )}
          </div>
          
          <div className="flex items-center gap-3 mb-3">
            {exercise.difficulty && (
              <span className={`px-2 py-1 rounded text-xs ${difficultyColors[exercise.difficulty]}`}>
                {exercise.difficulty}
              </span>
            )}
            {exercise.duration && (
              <span className="text-xs text-slate-400">• {exercise.duration}</span>
            )}
            {exercise.type && (
              <span className="text-xs text-slate-400">• {exercise.type.replace('_', ' ')}</span>
            )}
          </div>
          
          <MarkdownText className="text-slate-300 text-sm mb-3">
            {exercise.description}
          </MarkdownText>
          
          {isActive && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-4 pt-4 border-t border-slate-600"
            >
              <h5 className="text-white font-medium mb-3">Instructions:</h5>
              <ol className="space-y-2 text-sm text-slate-300">
                {(exercise.instructions ?? exercise.steps ?? []).map((instruction, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span className="text-conversation-teal font-medium">{idx + 1}.</span>
                    <MarkdownText>{instruction}</MarkdownText>
                  </li>
                ))}
              </ol>
              
              {exercise.scienceConnection && (
                <div className="mt-4 p-3 bg-blue-900/20 rounded-lg">
                  <h6 className="text-blue-300 font-medium mb-1 flex items-center gap-2">
                    <Beaker className="w-4 h-4" />
                    Science Connection
                  </h6>
                  <MarkdownText className="text-slate-300 text-sm">
                    {exercise.scienceConnection}
                  </MarkdownText>
                </div>
              )}
              
              {exercise.realWorldVariation && (
                <div className="mt-4 p-3 bg-green-900/20 rounded-lg">
                  <h6 className="text-green-300 font-medium mb-1 flex items-center gap-2">
                    <Coffee className="w-4 h-4" />
                    Real World Variation
                  </h6>
                  <MarkdownText className="text-slate-300 text-sm">
                    {exercise.realWorldVariation}
                  </MarkdownText>
                </div>
              )}
              
              {!isCompleted && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onComplete();
                  }}
                  className="mt-4"
                >
                  Mark Complete
                </Button>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </Card>
  );
};

// Conversation Challenge Card
const ConversationChallengeCard = ({ challenge, isCompleted, onComplete }) => {
  const [showReflection, setShowReflection] = useState(false);

  return (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-4 h-4 text-purple-400" />
          </div>
          <div className="flex-1">
            <h5 className="text-white font-medium mb-1">Scenario: {challenge.scenario}</h5>
            <p className="text-purple-300 text-sm mb-2">{challenge.challenge}</p>
            
            {!showReflection && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowReflection(true)}
              >
                Show Reflection Prompt
              </Button>
            )}
            
            {showReflection && (
              <div className="mt-3 p-3 bg-slate-800/50 rounded-lg">
                <p className="text-slate-300 text-sm italic">{challenge.reflection}</p>
                {!isCompleted && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onComplete}
                    className="mt-2"
                  >
                    Mark Complete
                  </Button>
                )}
              </div>
            )}
          </div>
          {isCompleted && (
            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
          )}
        </div>
      </div>
    </Card>
  );
};

// Main Module Detail Component
const ConversationModuleDetail = ({ module, onBack, onComplete }) => {
  const { 
    getModuleProgress, 
    updateModuleProgress,
    completeModule,
    isModuleCompleted
  } = useApp();
  
  const savedProgress = getModuleProgress(module.id) || {};
  const moduleIsCompleted = isModuleCompleted(module.id);

  // Art of Story keeps each module's exercises under content.exercises. This
  // renderer came from The Art of Conversation, which kept them (and a list
  // of real-world challenges) at the top level, so every module showed an
  // empty Exercises page and an empty Real-World Challenges page.
  const exercises = module.content?.exercises ?? module.exercises ?? [];
  const challenges = module.conversationChallenges ?? [];

  const [currentPage, setCurrentPage] = useState(savedProgress.currentPage || 0);
  const [completedSections, setCompletedSections] = useState(savedProgress.completedSections || []);
  const [completedExercises, setCompletedExercises] = useState(savedProgress.completedExercises || []);
  const [completedChallenges, setCompletedChallenges] = useState(savedProgress.completedChallenges || []);
  const [activeExercise, setActiveExercise] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState("Section Complete!");
  const [showCertificate, setShowCertificate] = useState(false);
  
  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);
  
  // Create pages from module content
  const pages = [
    { type: 'overview', title: 'Overview', icon: BookOpen },
    ...module.content.sections.map((section, idx) => ({
      type: 'section',
      index: idx,
      title: section.title,
      icon: Brain
    })),
    ...(exercises.length ? [{ type: 'exercises', title: 'Exercises', icon: Target }] : []),
    ...(challenges.length ? [{ type: 'challenges', title: 'Real-World Challenges', icon: MessageSquare }] : []),
    { type: 'takeaways', title: 'Key Takeaways', icon: Award }
  ];

  // Saved progress may point past the end if the page list changed.
  const currentPageData = pages[Math.min(currentPage, pages.length - 1)];

  // The page being read is the section Marlow hears about if the teller opens
  // her from here (the overview names no section).
  const readingSection = currentPageData && currentPageData.type !== 'overview' ? currentPageData.title : null;
  useEffect(() => {
    setReadingSection(module.id, readingSection);
    return () => setReadingSection(module.id, null);
  }, [module.id, readingSection]);
  
  useEffect(() => {
    if (currentPage > pages.length - 1) setCurrentPage(pages.length - 1);
  }, [currentPage, pages.length]);

  // Progress counts sections. Before the Exercises page showed anything no
  // exercise could be completed, so counting them now would lower the saved
  // percentage of modules people have already finished.
  const totalItems = module.content.sections.length;
  const completedItems = completedSections.length;
  
  const progressPercentage = Math.round((completedItems / totalItems) * 100);

  // Save progress
  useEffect(() => {
    const saveProgress = async () => {
      await updateModuleProgress(module.id, {
        currentPage,
        completedSections,
        completedExercises,
        completedChallenges,
        progressPercentage,
        lastUpdated: new Date().toISOString()
      });
    };

    const timeoutId = setTimeout(saveProgress, 500);
    return () => clearTimeout(timeoutId);
  }, [currentPage, completedSections, completedExercises, completedChallenges, module.id]);

  const handleSectionComplete = (sectionIndex) => {
    if (!completedSections.includes(sectionIndex)) {
      setCompletedSections([...completedSections, sectionIndex]);
    }
  };

  const handleExerciseComplete = (exerciseIndex) => {
    if (!completedExercises.includes(exerciseIndex)) {
      setCompletedExercises([...completedExercises, exerciseIndex]);
    }
  };

  const handleChallengeComplete = (challengeIndex) => {
    if (!completedChallenges.includes(challengeIndex)) {
      setCompletedChallenges([...completedChallenges, challengeIndex]);
    }
  };

  const handleModuleComplete = async () => {
    await completeModule(module.id, {
      completedSections,
      completedExercises,
      completedChallenges,
      progressPercentage: 100,
      completedAt: new Date().toISOString()
    });

    // Show certificate modal
    setShowCertificate(true);

    // Call onComplete callback after a delay (after user closes certificate)
    // onComplete?.();
  };

  const handleContinue = () => {
    const pageType = currentPageData.type;

    // Auto-complete current page
    if (pageType === 'section' && !completedSections.includes(currentPageData.index)) {
      handleSectionComplete(currentPageData.index);
    } else if (pageType === 'exercises' && exercises.length > 0) {
      // Mark all exercises as complete
      const allExerciseIndices = exercises.map((_, idx) => idx);
      setCompletedExercises(allExerciseIndices);
    } else if (pageType === 'challenges' && challenges.length > 0) {
      // Mark all challenges as complete
      const allChallengeIndices = challenges.map((_, idx) => idx);
      setCompletedChallenges(allChallengeIndices);
    }

    // Show celebration
    setCelebrationMessage(
      currentPage === pages.length - 1 ? "Module Complete! 🎉" : "Well Done! 🌟"
    );
    setShowCelebration(true);

    // Move to next page after celebration
    setTimeout(() => {
      if (currentPage < pages.length - 1) {
        setCurrentPage(currentPage + 1);
      }
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal to-gunmetal pt-24 md:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-pearl/60 hover:text-pearl mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Modules
        </button>

        {/* Module Header with Centered Image Icon */}
        <div className="relative mb-8 rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-gunmetal to-charcoal">
          {/* Content */}
          <div className="relative p-8">
            <div className="flex flex-col items-center text-center mb-6">
              {/* Centered Full Image Icon */}
              {module.heroImage ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6"
                >
                  <img
                    src={module.heroImage}
                    alt={module.title}
                    className="w-44 h-44 object-contain rounded-2xl"
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05, rotate: 5 }}
                  className="w-32 h-32 bg-gradient-to-br from-conversation-teal via-bright-gold to-purple-500 rounded-2xl flex items-center justify-center shadow-lg mb-6"
                >
                  <BookOpen className="w-16 h-16 text-slate-900" />
                </motion.div>
              )}

              <div className="mb-6">
                <h1 className="text-4xl font-bold text-white mb-2">
                  {module.title}
                </h1>
                <p className="text-xl text-cyan-300">{module.subtitle}</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
              <div className="flex-1 w-full">

                <MarkdownText className="text-pearl/90 mb-6 text-base md:text-lg leading-relaxed text-center">
                  {module.description}
                </MarkdownText>

                <div className="flex items-center justify-center flex-wrap gap-2 md:gap-4 text-xs md:text-sm">
                  <span className="flex items-center gap-1 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 text-conversation-teal" />
                    <span className="text-pearl font-medium">{module.duration}</span>
                  </span>
                  {module.difficulty && (
                    <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full font-medium border backdrop-blur-sm ${
                      module.difficulty === 'Foundation'
                        ? 'bg-green-500/20 text-green-300 border-green-500/30'
                        : module.difficulty === 'Essential'
                        ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
                        : 'bg-red-500/20 text-red-300 border-red-500/30'
                    }`}>
                      {module.difficulty}
                    </span>
                  )}
                  {module.prerequisites && module.prerequisites.length > 0 && (
                    <span className="px-3 md:px-4 py-1.5 md:py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 text-pearl">
                      Prerequisites: {module.prerequisites.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Progress Circle */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex-shrink-0"
              >
                <div className="relative w-24 h-24 md:w-32 md:h-32 bg-white/10 backdrop-blur-sm rounded-full p-2 border-2 border-white/20">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 112 112">
                    <circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                    />
                    <motion.circle
                      cx="56" cy="56" r="50"
                      fill="none"
                      stroke="url(#progressGradient)"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - progressPercentage/100)}`}
                      strokeLinecap="round"
                      initial={{ strokeDashoffset: `${2 * Math.PI * 50}` }}
                      animate={{ strokeDashoffset: `${2 * Math.PI * 50 * (1 - progressPercentage/100)}` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                    />
                    <defs>
                      <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#14b8a6" />
                        <stop offset="100%" stopColor="#d4af37" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-white">{progressPercentage}%</span>
                    {moduleIsCompleted && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring" }}
                      >
                        <CheckCircle className="w-6 h-6 md:w-8 md:h-8 text-green-400 mt-1" />
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Progress Dots Only */}
        {/* Each dot sits in a 28 px target (WCAG 2.5.8). The hover scale is on
            the inner dot: framer's whileHover stays applied after a tap on
            touch screens, so scaling the button grew it over its neighbours. */}
        <div className="flex flex-wrap items-center justify-center mb-8">
          {pages.map((page, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx)}
              className="group h-7 min-w-[28px] px-2 flex items-center justify-center"
              title={page.title}
              aria-label={`Page ${idx + 1}: ${page.title}`}
              aria-current={idx === currentPage ? 'step' : undefined}
            >
              <span
                className={`block rounded-full transition-all group-hover:scale-125 ${
                  idx === currentPage
                    ? 'w-10 h-3 bg-gradient-to-r from-conversation-teal to-bright-gold'
                    : idx < currentPage
                    ? 'w-3 h-3 bg-green-500'
                    : 'w-3 h-3 bg-slate-600 group-hover:bg-slate-500'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Page Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Overview Page */}
            {currentPageData.type === 'overview' && (
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Module Overview</h2>

                <div className="bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 border-l-4 border-indigo-500 rounded-r-xl p-6 mb-8">
                  <MarkdownText className="text-slate-200 text-lg leading-relaxed first-letter:text-5xl first-letter:font-bold first-letter:text-indigo-400 first-letter:mr-3 first-letter:float-left">
                    {module.content.overview}
                  </MarkdownText>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-400" />
                    Learning Objectives
                  </h3>
                  <ul className="space-y-3">
                    {module.content.learningObjectives.map((objective, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-teal-300 text-sm">{idx + 1}</span>
                        </div>
                        <MarkdownText className="text-pearl/80">
                          {objective}
                        </MarkdownText>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            )}

            {/* Section Pages */}
            {currentPageData.type === 'section' && (
              <SectionContent
                section={module.content.sections[currentPageData.index]}
                sectionIndex={currentPageData.index}
                isCompleted={completedSections.includes(currentPageData.index)}
                onComplete={() => handleSectionComplete(currentPageData.index)}
              />
            )}

            {/* Exercises Page */}
            {currentPageData.type === 'exercises' && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Practice Exercises</h2>
                  <p className="text-slate-300 mb-6">
                    These exercises help you put what you've learned into practice with your own stories.
                    Open one to see its steps.
                  </p>
                </Card>
                
                {exercises.map((exercise, idx) => (
                  <ExerciseCard
                    key={idx}
                    exercise={exercise}
                    index={idx}
                    isCompleted={completedExercises.includes(idx)}
                    onComplete={() => handleExerciseComplete(idx)}
                    isActive={activeExercise === idx}
                    onClick={() => setActiveExercise(activeExercise === idx ? null : idx)}
                  />
                ))}
              </div>
            )}

            {/* Challenges Page */}
            {currentPageData.type === 'challenges' && (
              <div className="space-y-6">
                <Card className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-4">Real-World Challenges</h2>
                  <p className="text-slate-300 mb-6">
                    Take what you've learned into the stories you tell every day.
                    These challenges help you practise in real situations.
                  </p>
                </Card>
                
                <div className="space-y-4">
                  {challenges.map((challenge, idx) => (
                    <ConversationChallengeCard
                      key={idx}
                      challenge={challenge}
                      index={idx}
                      isCompleted={completedChallenges.includes(idx)}
                      onComplete={() => handleChallengeComplete(idx)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Takeaways Page */}
            {currentPageData.type === 'takeaways' && (
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Key Takeaways</h2>

                {(module.keyTakeaways || module.content?.keyTakeaways) && (
                  <div className="space-y-4 mb-8">
                    {(module.keyTakeaways || module.content.keyTakeaways).map((takeaway, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <MarkdownText className="text-slate-300">
                          {takeaway}
                        </MarkdownText>
                      </div>
                    ))}
                  </div>
                )}

                {(module.nextSteps || module.content?.nextSteps) && (
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4">Next Steps</h3>
                    <div className="space-y-3">
                      {(module.nextSteps || module.content.nextSteps).map((step, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                          <ArrowRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                          <MarkdownText className="text-pearl/80">
                            {step}
                          </MarkdownText>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {progressPercentage >= 80 && (
                  <div className="mt-8 text-center">
                    <Button
                      onClick={handleModuleComplete}
                      size="lg"
                      className="bg-gradient-to-r from-conversation-teal to-bright-gold"
                    >
                      <Award className="w-5 h-5 mr-2" />
                      Complete Module
                    </Button>
                  </div>
                )}
              </Card>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Bottom Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4"
        >
          <Button
            variant="ghost"
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
            className="flex items-center justify-center gap-2 order-2 sm:order-1"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Back</span>
          </Button>

          <Button
            onClick={handleContinue}
            disabled={currentPage === pages.length - 1 && moduleIsCompleted}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-conversation-teal to-bright-gold text-white px-6 sm:px-8 py-3 text-sm sm:text-base font-semibold hover:shadow-lg hover:shadow-conversation-teal/30 transition-all order-1 sm:order-2"
          >
            {currentPage === pages.length - 1 ? (
              <>
                <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                Complete Module
              </>
            ) : (
              <>
                <span className="hidden sm:inline">Mark as Complete & Continue</span>
                <span className="sm:hidden">Continue</span>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </>
            )}
          </Button>
        </motion.div>

        {/* Celebration Modal */}
        <CelebrationModal
          show={showCelebration}
          onClose={() => setShowCelebration(false)}
          message={celebrationMessage}
        />

        {/* Completion Certificate */}
        <CompletionCertificate
          show={showCertificate}
          onClose={() => {
            setShowCertificate(false);
            onComplete?.();
          }}
          module={module}
          userName="Learner"
        />
      </div>
    </div>
  );
};

export default ConversationModuleDetail;